import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, Mail, Phone, UserCheck, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const statusColors = {
  prospect: "bg-slate-100 text-slate-700",
  in_discussion: "bg-blue-100 text-blue-700",
  committed: "bg-amber-100 text-amber-700",
  invested: "bg-emerald-100 text-emerald-700",
  exited: "bg-rose-100 text-rose-700",
};

const typeColors = {
  holding_company: "bg-lime-100 text-zinc-900",
  angel_investor: "bg-lime-100 text-zinc-900",
  private_equity: "bg-slate-100 text-slate-700",
  family_office: "bg-amber-100 text-amber-700",
  personal_investor: "bg-emerald-100 text-emerald-700",
};

const typeLabels = {
  holding_company: "Holding selskap",
  angel_investor: "Engle investor",
  private_equity: "Private Equity",
  family_office: "Family office",
  personal_investor: "Personlig investor",
};

export default function InvestorCard({ investor, investments = [], onClick }) {
  const totalInvestment = investments.reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);
  const totalOwnership = investments.reduce((sum, inv) => sum + (inv.ownership_percentage || 0), 0);
  const companiesCount = investments.length;
  return (
    <Card 
      className="p-5 bg-white border-0 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-lime-100 to-lime-200 flex items-center justify-center">
            <User className="w-5 h-5 text-zinc-900" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 group-hover:text-zinc-900 transition-colors">
              {investor.name}
            </h3>
            {investor.contact_name && (
              <p className="text-sm text-slate-500">{investor.contact_name}</p>
            )}
          </div>
        </div>
        <Badge className={cn("font-medium border-0", statusColors[investor.status])}>
          {investor.status?.replace("_", " ")}
        </Badge>
      </div>
      
      <div className="space-y-2 mb-4">
        {investor.contact_email && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Mail className="w-3.5 h-3.5" />
            <span className="truncate">{investor.contact_email}</span>
          </div>
        )}
        {investor.contact_phone && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Phone className="w-3.5 h-3.5" />
            <span>{investor.contact_phone}</span>
          </div>
        )}
        {investor.responsible_person_name && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <UserCheck className="w-3.5 h-3.5" />
            <span>Ansvarlig: {investor.responsible_person_name}</span>
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2">
          {investor.type && (
            <Badge variant="secondary" className={cn("text-xs border-0", typeColors[investor.type])}>
              {typeLabels[investor.type] || investor.type}
            </Badge>
          )}
          {investor.relationship_health_score !== undefined && investor.relationship_health_score !== null && (
            <Badge 
              variant="outline" 
              className={cn(
                "text-xs",
                investor.relationship_health_score >= 80 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                investor.relationship_health_score >= 60 ? "bg-amber-50 text-amber-700 border-amber-200" :
                investor.relationship_health_score >= 40 ? "bg-orange-50 text-orange-700 border-orange-200" :
                "bg-red-50 text-red-700 border-red-200"
              )}
            >
              <Activity className="w-3 h-3 mr-1" />
              {investor.relationship_health_score}
            </Badge>
          )}
          {companiesCount > 0 && (
            <span className="text-xs text-slate-500">
              {companiesCount} {companiesCount === 1 ? 'company' : 'companies'}
            </span>
          )}
        </div>
        {totalInvestment > 0 && (
          <span className="text-sm font-semibold text-emerald-600">
            {totalInvestment.toLocaleString()} kr
          </span>
        )}
      </div>
    </Card>
  );
}